import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Grid, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, TextField,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { billingApi, tenantApi, complianceAddonApi, entitlementsApi } from '../services/api'
import BundlesPanel from '../components/BundlesPanel'
import { RootState } from '../store'

const BillingPage = () => {
  const [subscription, setSubscription] = useState<any>(null)
  const [invoices, setInvoices] = useState<any[]>([])
  const [usage, setUsage] = useState<any>(null)
  const [tenant, setTenant] = useState<any>(null)
  const [quotas, setQuotas] = useState<any[]>([])
  // Bundles are what the contract names; the plan and quotas below are how it
  // is metered. Billing showed only the latter, so a customer could not see
  // on their own billing page which bundles they held.
  const [entitlements, setEntitlements] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  const searchParams = new URLSearchParams(window.location.search)
  const paymentSuccess = searchParams.get('payment') === 'success'
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelDetails, setCancelDetails] = useState('')
  const [cancelReasons, setCancelReasons] = useState<string[]>([])
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    if (!tenantId) { setLoading(false); return }
    const fetch = async () => {
      const results = await Promise.allSettled([
        billingApi.getTenantSubscription(tenantId),
        billingApi.getTenantInvoices(tenantId),
        billingApi.getTenantUsage(tenantId),
        tenantApi.getTenant(tenantId),
        tenantApi.getTenantQuotas(tenantId),
        entitlementsApi.get(tenantId),
      ])
      if (results[0].status === 'fulfilled') setSubscription(results[0].value.data)
      if (results[1].status === 'fulfilled') setInvoices(results[1].value.data || [])
      if (results[2].status === 'fulfilled') setUsage(results[2].value.data)
      if (results[3].status === 'fulfilled') setTenant(results[3].value.data)
      if (results[4].status === 'fulfilled') setQuotas(results[4].value.data || [])
      if (results[5].status === 'fulfilled') setEntitlements(results[5].value.data)
      setLoading(false)
    }
    fetch()
  }, [tenantId])

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>

  if (!tenantId) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6">Please sign in to view billing</Typography>
      <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/')}>Sign In</Button>
    </Box>
  )

  const statusColor = (s: string) => {
    if (s === 'active') return 'success'
    if (s === 'trialing' || s === 'trial') return 'info'
    if (s === 'grace_period') return 'warning'
    return 'error'
  }

  return (
    <Box>
      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment received! Thank you. Your subscription is being activated — this may take a few moments.
        </Alert>
      )}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Billing</Typography>
        <Button variant="contained" onClick={() => navigate('/pricing')}>
          {tenant?.tier === 'trial' ? 'Upgrade' : 'Change Plan'}
        </Button>
      </Box>

      <BundlesPanel
        bundles={entitlements?.bundles}
        features={entitlements?.features}
        tenantId={tenantId}
      />

      <Grid container spacing={3}>
        {/* Subscription card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Subscription</Typography>
              {subscription ? (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={subscription.plan_type} />
                    <Chip label={subscription.status} color={statusColor(subscription.status) as any} />
                    <Chip label={subscription.billing_cycle} variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Current period: {new Date(subscription.current_period_start).toLocaleDateString()} - {new Date(subscription.current_period_end).toLocaleDateString()}
                  </Typography>
                  {subscription.total_amount > 0 && (
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      ${Number(subscription.total_amount).toLocaleString()}/{subscription.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                    </Typography>
                  )}
                </Box>
              ) : tenant?.status === 'trial' ? (
                <Box>
                  <Chip label="Trial" color="info" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {tenant.trial_ends_at
                      ? `Expires: ${new Date(tenant.trial_ends_at).toLocaleDateString()}`
                      : 'Trial clock starts on first scan'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No active subscription</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Add-ons */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Add-ons</Typography>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>Compliance Posture</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Detect, track, and report agent alignment to EU AI Act, NIST AI RMF, ISO 42001, SOC 2, GDPR, OWASP and MAESTRO.
                  </Typography>
                </Box>
                {subscription?.has_compliance_addon ? (
                  <Chip label="Active" color="success" size="small" />
                ) : tenant?.tier === 'enterprise' ? (
                  <Chip label="Included" color="primary" size="small" />
                ) : tenant?.tier === 'business' ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={async () => {
                      try {
                        const resp = await complianceAddonApi.startCheckout({
                          tenant_id: tenantId!,
                          billing_email: user?.email || '',
                          tenant_name: tenant?.name || '',
                        })
                        if (resp.data?.checkout_url) window.location.href = resp.data.checkout_url
                      } catch (e: any) {
                        alert(e?.response?.data?.detail || 'Unable to start checkout')
                      }
                    }}
                  >
                    Add $499/mo
                  </Button>
                ) : (
                  <Chip label="Business plan required" size="small" variant="outlined" />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage gauges */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Usage This Period</Typography>
              {quotas.length > 0 ? quotas.map((q: any) => (
                <Box key={q.resource} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {q.resource.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2">
                      {q.current_usage} / {q.max_allowed >= 999999 ? 'Unlimited' : q.max_allowed}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={q.max_allowed >= 999999 ? 0 : Math.min(q.percentage_used, 100)}
                    color={q.percentage_used >= 80 ? 'error' : q.percentage_used >= 60 ? 'warning' : 'primary'}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )) : (
                <Typography variant="body2" color="text.secondary">No usage data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invoices */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Invoices</Typography>
              {invoices.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((inv: any) => (
                        <TableRow key={inv.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {inv.invoice_number}
                          </TableCell>
                          <TableCell>{new Date(inv.invoice_date).toLocaleDateString()}</TableCell>
                          <TableCell>${Number(inv.total_amount).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={inv.status}
                              size="small"
                              color={inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">No invoices yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {tenant?.status === 'active' && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={async () => {
              try {
                const res = await billingApi.getCancellationReasons()
                setCancelReasons(res.data || [])
              } catch { setCancelReasons(['Too expensive', 'Not enough value', 'Other']) }
              setCancelOpen(true)
            }}
          >
            Cancel Subscription
          </Button>
        </Box>
      )}

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We're sorry to see you go. Please let us know why so we can improve.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select value={cancelReason} label="Reason" onChange={e => setCancelReason(e.target.value)}>
              {cancelReasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          {cancelReason === 'Other' && (
            <TextField
              fullWidth multiline rows={3} label="Please describe"
              value={cancelDetails} onChange={e => setCancelDetails(e.target.value)}
            />
          )}
          {cancelReason && cancelReason !== 'Other' && (
            <TextField
              fullWidth multiline rows={2} label="Additional details (optional)"
              value={cancelDetails} onChange={e => setCancelDetails(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelOpen(false); setCancelReason(''); setCancelDetails('') }}>
            Keep Subscription
          </Button>
          <Button
            color="error" variant="contained" disabled={!cancelReason || cancelling}
            onClick={async () => {
              setCancelling(true)
              try {
                await billingApi.createCancellationRequest({
                  tenant_id: tenantId!,
                  tenant_name: tenant?.name,
                  billing_email: user?.email,
                  reason: cancelReason,
                  reason_details: cancelDetails || undefined,
                })
                setCancelOpen(false)
                setCancelReason('')
                setCancelDetails('')
                alert('Cancellation request submitted. Our team will review it shortly.')
              } catch (err: any) {
                alert(err.response?.data?.detail || 'Failed to submit cancellation request')
              }
              setCancelling(false)
            }}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Submit Cancellation Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BillingPage
